import { apiClient as api } from "./httpClient";

/**
 * Assinatura eletrônica (item 67). Conexão da unidade com o provedor (Tecnologia > Assinatura eletrônica,
 * permissão `signature-settings`) e o ciclo de assinatura do contrato (enviar, atualizar, anexar o
 * assinado à mão, baixar). A chave da clínica só VAI para a API; nunca volta.
 */
export interface SignatureProviderOption {
  code: string;
  name: string;
  usesApiKey: boolean;
}

export type SignatureEnvironment = "sandbox" | "production";

export interface SignatureConnection {
  id: string;
  providerCode: string;
  providerName: string;
  environment: SignatureEnvironment;
  accountId: string | null;
  accountName: string | null;
  webhookRegistered: boolean;
  connectedByName: string | null;
  connectedAt: string;
  lastCheckedAt: string | null;
}

export interface SignatureConnectionStatus {
  serverConfigured: boolean;
  webhookAvailable: boolean;
  connection: SignatureConnection | null;
}

export type SignatureChannel = "email" | "whatsapp";

export interface ContractSignerInput {
  role: string;
  name: string;
  email: string | null;
  phone: string | null;
  documentNumber: string | null;
  channel: SignatureChannel;
  order: number;
}

export type ContractSignatureStatus = "sent" | "signed" | "rejected" | "expired" | "cancelled" | "failed";

export const SIGNATURE_STATUS_LABELS: Record<ContractSignatureStatus, string> = {
  sent: "Aguardando assinatura",
  signed: "Assinado",
  rejected: "Recusado",
  expired: "Expirado",
  cancelled: "Envio cancelado",
  failed: "Falha no envio",
};

const ERRORS: Record<string, string> = {
  "Signatures.NotConfigured": "A assinatura eletrônica não está configurada neste servidor. Avise o suporte do Clinic4Us.",
  "Signatures.NoConnection": "Esta unidade não tem provedor de assinatura conectado. O Administrador conecta em Tecnologia > Assinatura eletrônica.",
  "Signatures.AlreadyInSignature": "Este contrato já está em assinatura ou assinado.",
  "Signatures.ContractCancelled": "Contrato cancelado não vai para assinatura.",
  "Signatures.SignersRequired": "Informe pelo menos um assinante.",
  "Signatures.SignedFileRequired": "Anexe o PDF do contrato assinado (até 20 MB).",
  "Signatures.SignedFileNotPdf": "O arquivo anexado não é um PDF.",
  "Signatures.SignedFileNotFound": "Este contrato ainda não tem o PDF assinado guardado.",
  "Signatures.StorageUnavailable": "Armazenamento de arquivos indisponível. Tente de novo em instantes.",
  "Signatures.CredentialRequired": "Informe a chave de API do provedor.",
  "Signatures.EnvironmentInvalid": "Escolha o ambiente: teste ou produção.",
};

export function signatureErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { code?: string; description?: string } } })?.response?.data;
  if (data?.code && ERRORS[data.code]) return ERRORS[data.code];
  // Recusa do provedor e regras de assinante já vêm em português com o detalhe
  if (data?.code?.startsWith("Signatures.") && data.description) return data.description;
  return fallback;
}

export const signatureSettingsService = {
  async getProviders(): Promise<SignatureProviderOption[]> {
    const response = await api.get<SignatureProviderOption[]>("/signature-settings/providers");
    return response.data;
  },
  async getStatus(): Promise<SignatureConnectionStatus> {
    const response = await api.get<SignatureConnectionStatus>("/signature-settings");
    return response.data;
  },
  async connect(payload: { providerCode: string; apiKey: string; accountId: string | null; environment: SignatureEnvironment }): Promise<SignatureConnection> {
    const response = await api.post<SignatureConnection>("/signature-settings", payload);
    return response.data;
  },
  async test(): Promise<SignatureConnection> {
    const response = await api.post<SignatureConnection>("/signature-settings/test");
    return response.data;
  },
  async disconnect(): Promise<void> {
    await api.delete("/signature-settings");
  },
};

export const contractSignatureService = {
  async send(contractId: string, signers: ContractSignerInput[], message: string | null): Promise<ContractSignatureStatus> {
    const response = await api.post<{ status: ContractSignatureStatus }>(`/contracts/${contractId}/signature`, { signers, message });
    return response.data.status;
  },
  async sync(contractId: string): Promise<ContractSignatureStatus | null> {
    const response = await api.post<{ status: ContractSignatureStatus | null }>(`/contracts/${contractId}/signature/sync`);
    return response.data.status;
  },
  // O apiClient manda application/json por padrão e isso vaza para o FormData (415); anulando, o browser põe o boundary
  async attachSigned(contractId: string, file: File): Promise<void> {
    const form = new FormData();
    form.append("file", file, file.name);
    await api.post(`/contracts/${contractId}/signature/manual`, form, { headers: { "Content-Type": null } });
  },
  /** Abre o PDF guardado (assinado ou a prova) numa aba nova. */
  async open(contractId: string, evidence: boolean): Promise<void> {
    const response = await api.get(`/contracts/${contractId}/signature/file`, { params: { evidence }, responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    window.open(url, "_blank");
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};
